import { useState } from 'react'
import { useOverlayTriggerState } from 'react-stately'
import { useReportBuilderStore } from '../stores/reportBuilderStore'
import { ConfirmDialog } from './ConfirmDialog'
import { IconButton } from './IconButton'

function formatSaveDate(date: Date | string | undefined): string {
	if (!date) return ''
	const d = typeof date === 'string' ? new Date(date) : date
	const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
	const month = months[d.getMonth()]
	const day = d.getDate()
	const year = d.getFullYear().toString().slice(-2)
	return `${month} ${day}, '${year}`
}

export function SavedConfigs() {
	const { savedConfigs, loadConfig, deleteConfig, resetConfig, currentConfig, isSaving } = useReportBuilderStore()

	const isSelected = (configId: string | undefined) => configId && currentConfig.id === configId

	const deleteConfirmState = useOverlayTriggerState({})
	const newReportConfirmState = useOverlayTriggerState({})
	const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

	const pendingDeleteName = pendingDeleteId
		? (savedConfigs.find((c) => c.id === pendingDeleteId)?.name ?? 'this report')
		: ''

	const hasWorkInProgress = !!currentConfig.id || !!currentConfig.name.trim()

	const handleNewReport = () => {
		if (hasWorkInProgress) {
			if (!newReportConfirmState.isOpen) newReportConfirmState.open()
		} else {
			resetConfig()
		}
	}

	const handleDeleteClick = (configId: string) => {
		if (!deleteConfirmState.isOpen) {
			setPendingDeleteId(configId)
			deleteConfirmState.open()
		}
	}

	return (
		<div className='bg-white border border-neutral-200 rounded-lg shadow-sm'>
			<div className='border-b border-neutral-200 px-4 py-3'>
				<h3 className='font-semibold text-neutral-900'>Saved Reports</h3>
			</div>
			<div className='p-4'>
				<button
					type='button'
					onClick={handleNewReport}
					className='w-full mb-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium'>
					+ New Report
				</button>

				{savedConfigs.length === 0 ? (
					<p className='text-sm text-neutral-500 text-center py-4'>
						No saved reports yet. Create and save a report configuration to see it here.
					</p>
				) : (
					<ul className='space-y-2'>
						{savedConfigs.map((config) => {
							const selected = isSelected(config.id)
							return (
								<li
									key={config.id}
									className={`rounded-md p-3 transition-colors ${
										selected ? 'bg-blue-50 border-2 border-blue-500' : 'border border-neutral-200 hover:bg-neutral-50'
									}`}>
									<div className='flex items-start justify-between gap-2'>
										<div className='flex-1 min-w-0'>
											<button type='button' onClick={() => loadConfig(config)} className='text-left w-full'>
												<p className={`font-medium truncate ${selected ? 'text-blue-900' : 'text-neutral-900'}`}>
													{config.name}
												</p>
												{config.description && (
													<p className={`text-xs truncate mt-0.5 ${selected ? 'text-blue-600' : 'text-neutral-500'}`}>
														{config.description}
													</p>
												)}
												<p className={`text-xs mt-1 ${selected ? 'text-blue-500' : 'text-neutral-400'}`}>
													{config.reportType} • {config.selectedFormIds.length} form(s) •{' '}
													{config.selectedCycleIds.length} cycle(s)
												</p>
											</button>
										</div>
										<div className='flex flex-col items-end gap-1'>
											<IconButton
												label='Delete configuration'
												onPress={() => config.id && handleDeleteClick(config.id)}
												isDisabled={isSaving}
												className={`transition-colors p-1 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''} ${selected ? 'text-blue-400 hover:text-red-500' : 'text-neutral-400 hover:text-red-500'}`}>
												<svg
													className='h-4 w-4'
													fill='none'
													viewBox='0 0 24 24'
													stroke='currentColor'
													aria-hidden='true'>
													<path
														strokeLinecap='round'
														strokeLinejoin='round'
														strokeWidth={2}
														d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
													/>
												</svg>
											</IconButton>
											{(config.updatedAt || config.createdAt) && (
												<span
													className={`text-xs whitespace-nowrap ${selected ? 'text-blue-500' : 'text-neutral-400'}`}>
													{formatSaveDate(config.updatedAt || config.createdAt)}
												</span>
											)}
										</div>
									</div>
								</li>
							)
						})}
					</ul>
				)}
			</div>

			<ConfirmDialog
				title='Delete Report Configuration'
				message={`Are you sure you want to delete "${pendingDeleteName}"? This cannot be undone.`}
				confirmLabel='Delete'
				onConfirm={() => {
					if (pendingDeleteId) deleteConfig(pendingDeleteId)
					setPendingDeleteId(null)
				}}
				state={deleteConfirmState}
			/>

			<ConfirmDialog
				title='Start New Report'
				message='You have unsaved changes. Start a new report anyway?'
				confirmLabel='New Report'
				onConfirm={resetConfig}
				state={newReportConfirmState}
			/>
		</div>
	)
}
